
import { useState } from 'react';
import Button from '../../../components/base/Button';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    typeClient: 'particulier',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>(
    'idle'
  );

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.message.length > 500) {
      alert('Le message ne peut pas dépasser 500 caractères.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch(
        'https://readdy.ai/api/form/d3baqvaf3bgsbtd3kv1g',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams(formDataToSend as any).toString(),
        }
      );

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nom: '',
          prenom: '',
          email: '',
          telephone: '',
          typeClient: 'particulier',
          service: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gray-900 text-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">Contactez-nous</h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Prêt à sécuriser votre domicile ou entreprise ? Demandez votre devis
            gratuit
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Informations de contact */}
          <div>
            <div className="bg-gray-800 p-8 rounded-xl mb-8">
              <h3 className="text-2xl font-bold mb-6 text-orange-400">
                Informations de contact
              </h3>

              <div className="space-y-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center mr-4">
                    <i className="ri-phone-line text-xl text-white"></i>
                  </div>
                  <div>
                    <p className="text-gray-300 text-sm">Téléphone</p>
                    <a
                      href="tel:0484660814"
                      className="text-xl font-semibold hover:text-orange-400 transition-colors cursor-pointer"
                    >
                      0484 66 08 14
                    </a>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mr-4">
                    <i className="ri-mail-line text-xl text-white"></i>
                  </div>
                  <div>
                    <p className="text-gray-300 text-sm">Email</p>
                    <a
                      href="mailto:fms.protec@gmail.com"
                      className="text-xl font-semibold hover:text-blue-400 transition-colors cursor-pointer"
                    >
                      fms.protec@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center mr-4">
                    <i className="ri-time-line text-xl text-white"></i>
                  </div>
                  <div>
                    <p className="text-gray-300 text-sm">Disponibilité</p>
                    <p className="text-xl font-semibold">
                      Devis gratuit sur demande
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-orange-500 to-blue-500 p-8 rounded-xl text-center">
              <h4 className="text-2xl font-bold mb-4">
                Pourquoi choisir FMS Protec ?
              </h4>
              <ul className="text-left space-y-2">
                <li className="flex items-center">
                  <i className="ri-check-line mr-2"></i>
                  Devis gratuit et sans engagement
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line mr-2"></i>
                  Installation professionnelle
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line mr-2"></i>
                  Maintenance et support technique
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line mr-2"></i>
                  Solutions sur mesure
                </li>
              </ul>
            </div>
          </div>

          {/* Formulaire de contact */}
          <div className="bg-white text-gray-900 p-8 rounded-xl">
            <h3 className="text-2xl font-bold mb-6 text-center">
              Demandez votre devis gratuit
            </h3>

            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Prénom *
                  </label>
                  <input
                    type="text"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-5"
                    placeholder="Votre prénom"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Nom *
                  </label>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-5"
                    placeholder="Votre nom"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-5"
                    placeholder="votre@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-5"
                    placeholder="0484 66 08 14"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Type de client
                  </label>
                  <div className="relative">
                    <select
                      name="typeClient"
                      value={formData.typeClient}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-5"
                    >
                      <option value="particulier">Particulier</option>
                      <option value="pme">PME</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Service souhaité
                  </label>
                  <div className="relative">
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-5"
                    >
                      <option value="">Sélectionnez un service</option>
                      <option value="controle-acces">Contrôle d'accès</option>
                      <option value="alarme">Système d'alarme</option>
                      <option value="intrusion">Protection anti-intrusion</option>
                      <option value="incendie">Sécurité incendie</option>
                      <option value="multiple">Plusieurs services</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Message (max 500 caractères)
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-5"
                  placeholder="Décrivez votre projet ou posez vos questions..."
                ></textarea>
                <div className="text-right text-sm text-gray-500 mt-1">
                  {formData.message.length}/500 caractères
                </div>
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                onClick={() => {}}
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-2"></i>
                    Envoyer ma demande
                  </>
                )}
              </Button>

              {submitStatus === 'success' && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg text-center">
                  <i className="ri-check-circle-line mr-2"></i>
                  Votre demande a été envoyée avec succès ! Nous vous contacterons
                  rapidement.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg text-center">
                  <i className="ri-error-warning-line mr-2"></i>
                  Une erreur est survenue. Veuillez réessayer ou nous contacter
                  directement.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
